import React, { useEffect, useCallback } from 'react';
import { Box, Dialog, IconButton, Button } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { BRAND_SANS_FONT } from '../../shared/design/theme';
import { ArrowBackIcon } from '../../shared/icons';
import CloseIcon from '@mui/icons-material/Close';
import BlogArticleBody from './BlogArticleBody';
import { blogPosts } from './BlogPostPage';

/**
 * Lightbox modal that renders a blog article without leaving the landing page.
 *
 * - Centered, ~720px wide, 88vh tall
 * - Escape key + backdrop click close
 * - URL hash updates to `#blog/<slug>` for shareability
 * - Direct `/blog/:slug` route still works (BlogPostPage handles that)
 *
 * Controlled by the parent via `slug` prop (null/undefined = closed).
 */
export default function BlogPostModal({ slug, onClose }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const post = slug ? blogPosts[slug] : null;
  const open = Boolean(post);

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  // Scroll modal content back to top whenever the slug changes
  const contentRef = React.useRef(null);
  useEffect(() => {
    if (open && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [slug, open]);

  const handleClose = useCallback(
    (_event, reason) => {
      // Allow any close reason — Escape, backdrop, explicit button
      if (onClose) onClose(reason);
    },
    [onClose]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      aria-labelledby="blog-modal-title"
      sx={{
        '& .MuiDialog-container': {
          alignItems: { xs: 'stretch', sm: 'center' },
        },
        '& .MuiBackdrop-root': {
          backgroundColor: alpha('#1e1612', 0.55),
          backdropFilter: 'blur(6px)',
        },
        '& .MuiPaper-root': {
          width: '100%',
          maxWidth: { xs: '100%', sm: 760 },
          height: { xs: '100%', sm: '88vh' },
          maxHeight: { xs: '100%', sm: '88vh' },
          margin: { xs: 0, sm: 2 },
          borderRadius: { xs: 0, sm: 3 },
          overflow: 'hidden',
          background: theme.palette.background.default,
          boxShadow: `0 40px 80px -20px ${alpha('#1e1612', 0.35)}`,
          border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
        },
      }}
    >
      {/* Sticky close bar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 2, sm: 3 },
          py: 1.25,
          borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
          background: alpha(theme.palette.background.default, 0.85),
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box
          component="a"
          href={post ? `/blog/${slug}` : '#'}
          onClick={(e) => {
            // Plain click → close modal and navigate to the standalone
            // /blog/<slug> page (full reading experience with chrome).
            // Cmd/Ctrl/Shift-click → fall through to the browser's
            // default handler so it opens in a new tab.
            if (e.metaKey || e.ctrlKey || e.shiftKey) return;
            e.preventDefault();
            if (onClose) onClose();
            if (slug) navigate(`/blog/${slug}`);
          }}
          sx={{
            fontFamily: BRAND_SANS_FONT,
            fontSize: '0.7rem',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: alpha(theme.palette.text.primary, 0.45),
            textDecoration: 'none',
            '&:hover': { color: '#B83B1D' },
          }}
        >
          Open full page ↗
        </Box>

        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="Close article"
          sx={{
            color: alpha(theme.palette.text.primary, 0.6),
            '&:hover': {
              color: theme.palette.text.primary,
              background: alpha(theme.palette.text.primary, 0.06),
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Scrollable content */}
      <Box
        ref={contentRef}
        sx={{
          overflowY: 'auto',
          px: { xs: 3, sm: 6 },
          py: { xs: 4, sm: 6 },
          // Nice scrollbar
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.text.primary, 0.15),
            borderRadius: 4,
          },
        }}
      >
        <Box sx={{ maxWidth: 640, mx: 'auto' }}>
          <BlogArticleBody post={post} compact />

          <Box
            sx={{
              mt: { xs: 6, md: 8 },
              pt: 4,
              borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
            }}
          >
            <Button
              onClick={handleClose}
              variant="text"
              startIcon={<ArrowBackIcon />}
              sx={{
                fontFamily: BRAND_SANS_FONT,
                fontWeight: 500,
                textTransform: 'none',
                color: '#B83B1D',
                '&:hover': {
                  backgroundColor: 'rgba(184, 59, 29, 0.06)',
                },
              }}
            >
              Back to all articles
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
